/**
 * LAG Cosmetics Accessibility Module
 *
 * This module provides comprehensive accessibility utilities for the LA Girl website,
 * including dynamic alt text generation for product images and fixes for WCAG 2.2
 * Level A, AA, and AAA issues identified in the accessibility report.
 *
 * WCAG 2.2 Level A Issues Addressed:
 * - Alt text should not be meaningless image file names (F30 | 1.1.1)
 * - Alt text should not contain placeholders like 'picture' or 'spacer' (F30 | 1.1.1, 1.2.1)
 * - img elements must have an accessible name (F65)
 * - Headings should not be empty (ARIA 1.2 5.2.1)
 * - ARIA role=button elements must have accessible names (4.1.2)
 * - The label element should not be blank (4.1.2)
 * - Skip links should have valid targets (2.4.1)
 * - Links must have accessible names (F89)
 * - Interactive controls should not be nested (4.1.2)
 * - Elements with role=list must contain role=listitem (1.3.1)
 * - Form controls must have accessible names (F68)
 *
 * WCAG 2.2 Level AA Issues Addressed:
 * - Focus visibility should be clear and consistent (F78)
 *
 * WCAG 2.2 Level AAA Issues Addressed:
 * - External links opening in new windows should indicate this (F22)
 *
 * @version 2.0.0
 */

const LAGAccessibility = (function() {
    'use strict';

    // =========================================================================
    // CONFIGURATION
    // =========================================================================

    const config = {
        // Keywords that indicate lifestyle/general/shared images
        // Each keyword maps to a descriptive phrase for alt text
        generalKeywords: [
            'share', 'shared', 'common', 'video', 'general',
            'lifestyle', 'lifestyles', 'videos', 'public',
            'infograph', 'infographic', 'tutorial', 'howto',
            'before', 'after', 'result', 'results',
            'swatch', 'swatches', 'palette', 'collection',
            'model', 'beauty', 'look', 'inspiration'
        ],

        // Keyword to descriptive phrase mapping for alt text generation
        keywordDescriptions: {
            // Lifestyle/promotional
            'lifestyle': 'lifestyle',
            'lifestyles': 'lifestyle',
            'shared': 'shared promotional',
            'share': 'shared promotional',
            'common': 'shared promotional',
            'general': 'general promotional',
            'public': 'promotional',

            // Informational
            'infograph': 'infographic',
            'infographic': 'infographic',
            'tutorial': 'tutorial',
            'howto': 'how-to tutorial',

            // Results/comparison
            'before': 'before application',
            'after': 'after application',
            'result': 'result',
            'results': 'results',

            // Product display
            'swatch': 'color swatch',
            'swatches': 'color swatches',
            'palette': 'palette',
            'collection': 'collection',

            // Beauty/model shots
            'model': 'model showcase',
            'beauty': 'beauty',
            'look': 'look inspiration',
            'inspiration': 'inspiration',

            // Video
            'video': 'video thumbnail',
            'videos': 'video thumbnail'
        },

        // Patterns that should exclude images from being treated as general
        excludedPatterns: ['PROD_ALTIMG', 'TEMP', 'DRAFT', 'BACKUP'],

        // SKU pattern detection settings
        skuPatterns: {
            // Basic SKU pattern: letters followed by numbers (e.g., GES94, GNL123)
            basic: /[A-Z]{2,5}\d{2,5}/gi,
            // Enhanced pattern with extensions (e.g., GES94-01, GNL123_A)
            enhanced: /[A-Z]{2,5}\d{2,5}[A-Z0-9_-]*/gi,
            // Flexible separator handling
            separators: /[_\-\s\.]/g
        },

        // Image position indicators in filenames
        positionIndicators: {
            main: ['01', '-01', '_01', 'main', 'hero', 'primary'],
            swatch: ['02', '-02', '_02', 'swatch', 'color'],
            alternate: ['03', '04', '05', '-03', '-04', '-05', 'alt', 'alternate'],
            detail: ['detail', 'close', 'closeup', 'zoom']
        },

        // Debug mode - set to true for console logging
        debug: true,

        // =========================================================================
        // NEW ACCESSIBILITY FIXES CONFIGURATION (WCAG Report Issues)
        // =========================================================================

        // Issue: Empty headings should be hidden or given content
        emptyHeadingSelectors: [
            'h1:empty',
            'h2:empty',
            'h3:empty',
            'h4:empty',
            'h5:empty',
            'h6:empty',
            'h2.drawer-text:empty'
        ],

        // Issue: Elements with role=button that need accessible names
        buttonRoleSelectors: [
            '[role="button"]:not([aria-label]):not([aria-labelledby])',
            '.acsb-trigger[role="button"]:not([aria-label])'
        ],

        // Issue: Blank labels that need accessible text
        blankLabelSelectors: [
            'label:empty',
            '.product-tile__swatch:not([aria-label]):empty',
            'label[for="mobile-drawer"]:not([aria-label])'
        ],

        // Issue: Broken skip links - IDs that should exist
        skipLinkTargets: {
            'acsbContent': 'main, [role="main"], #MainContent, .main-content, .page-content',
            'acsbMenu': 'nav, [role="navigation"], .nav-menu, .header-nav, #MainNav',
            'acsbFooter': 'footer, [role="contentinfo"], .footer, #Footer'
        },

        // Issue: Links without accessible names (pictures, icons, etc.)
        linkSelectors: [
            'a:not([aria-label]):has(picture:only-child)',
            'a:not([aria-label]):has(img:only-child):not(:has(img[alt]:not([alt=""])))',
            'a.icon-class:not([aria-label])'
        ],

        // Issue: External links need to indicate they open in new window
        externalLinkSelectors: [
            'a[target="_blank"]:not([aria-describedby])'
        ],

        // Issue: Interactive controls nested inside other interactive controls
        nestedInteractiveSelectors: [
            'a button',
            'button a',
            'button label',
            '[role="button"] a',
            '[role="button"] button',
            'a [role="button"]'
        ],

        // Issue: Focus outline visibility
        focusEnhancementSelectors: [
            'a:focus',
            'button:focus',
            '[tabindex]:focus',
            'input:focus',
            'select:focus',
            'textarea:focus'
        ],

        // Non-descriptive link texts to enhance (AAA requirement)
        nonDescriptiveLinkTexts: [
            'click here',
            'here',
            'read more',
            'learn more',
            'more',
            'link',
            'go'
        ]
    };

    // =========================================================================
    // UTILITY FUNCTIONS
    // =========================================================================

    /**
     * Log debug messages when debug mode is enabled
     * @param {...any} args - Arguments to log
     */
    function debugLog(...args) {
        if (config.debug) {
        }
    }

    /**
     * Extract filename from a full image URL
     * @param {string} imageUrl - Full URL or path to image
     * @returns {string} Filename without extension
     */
    function extractFilename(imageUrl) {
        if (!imageUrl) return '';

        // Remove query parameters
        let cleanUrl = imageUrl.split('?')[0];

        // Get the last segment (filename with extension)
        let filename = cleanUrl.split('/').pop();

        // Remove file extension
        filename = filename.replace(/\.[^/.]+$/, '');

        debugLog('Extracted filename:', filename, 'from:', imageUrl);
        return filename;
    }

    /**
     * Normalize filename for comparison (lowercase, replace separators)
     * @param {string} filename - Filename to normalize
     * @returns {string} Normalized filename
     */
    function normalizeFilename(filename) {
        if (!filename) return '';
        return filename.toLowerCase().replace(config.skuPatterns.separators, ' ').trim();
    }

    /**
     * Extract SKU from a filename
     * @param {string} filename - Filename to parse
     * @returns {string|null} Extracted SKU or null if not found
     */
    function extractSkuFromFilename(filename) {
        if (!filename) return null;

        // Normalize the filename for SKU extraction
        const upperFilename = filename.toUpperCase();

        // Try to match SKU patterns
        const matches = upperFilename.match(config.skuPatterns.basic);

        if (matches && matches.length > 0) {
            // Return the first valid SKU found
            const sku = matches[0];
            debugLog('Extracted SKU:', sku, 'from filename:', filename);
            return sku;
        }

        debugLog('No SKU found in filename:', filename);
        return null;
    }

    /**
     * Check if filename contains any general/lifestyle keywords
     * @param {string} filename - Filename to check
     * @returns {boolean} True if general keyword found
     */
    function hasGeneralKeyword(filename) {
        if (!filename) return false;

        const normalizedFilename = normalizeFilename(filename);

        // Check for excluded patterns first
        for (const pattern of config.excludedPatterns) {
            if (filename.toUpperCase().includes(pattern)) {
                debugLog('Filename excluded by pattern:', pattern);
                return false;
            }
        }

        // Check for general keywords
        for (const keyword of config.generalKeywords) {
            if (normalizedFilename.includes(keyword.toLowerCase())) {
                debugLog('Found general keyword:', keyword, 'in filename:', filename);
                return true;
            }
        }

        return false;
    }

    /**
     * Determine image position/type based on filename
     * @param {string} filename - Filename to analyze
     * @returns {string} Image type: 'main', 'swatch', 'alternate', 'detail', or 'product'
     */
    function determineImagePosition(filename) {
        if (!filename) return 'product';

        const normalizedFilename = normalizeFilename(filename);

        for (const [type, indicators] of Object.entries(config.positionIndicators)) {
            for (const indicator of indicators) {
                if (normalizedFilename.includes(indicator.toLowerCase())) {
                    debugLog('Determined image position:', type, 'from indicator:', indicator);
                    return type;
                }
            }
        }

        return 'product';
    }

    /**
     * Find the matching general keyword in a filename
     * @param {string} filename - Filename to search
     * @returns {string|null} The found keyword or null
     */
    function findGeneralKeyword(filename) {
        if (!filename) return null;

        const normalizedFilename = normalizeFilename(filename);

        for (const keyword of config.generalKeywords) {
            if (normalizedFilename.includes(keyword.toLowerCase())) {
                return keyword;
            }
        }

        return null;
    }

    /**
     * Find ALL general keywords in a filename (may have multiple)
     * @param {string} filename - Filename to search
     * @returns {Array} Array of found keywords
     */
    function findAllGeneralKeywords(filename) {
        if (!filename) return [];

        const normalizedFilename = normalizeFilename(filename);
        const foundKeywords = [];

        for (const keyword of config.generalKeywords) {
            if (normalizedFilename.includes(keyword.toLowerCase())) {
                foundKeywords.push(keyword);
            }
        }

        return foundKeywords;
    }

    /**
     * Get descriptive phrase for a keyword
     * @param {string} keyword - Keyword to get description for
     * @returns {string} Descriptive phrase
     */
    function getKeywordDescription(keyword) {
        if (!keyword) return '';
        return config.keywordDescriptions[keyword.toLowerCase()] || keyword;
    }

    /**
     * Build a descriptive alt text phrase from found keywords
     * @param {Array} keywords - Array of keywords found in filename
     * @returns {string} Combined descriptive phrase
     */
    function buildKeywordPhrase(keywords) {
        if (!keywords || keywords.length === 0) return '';

        // Get unique descriptions (avoid duplicates like 'shared' and 'share')
        const descriptions = [];
        const seenDescriptions = new Set();

        for (const keyword of keywords) {
            const desc = getKeywordDescription(keyword);
            if (desc && !seenDescriptions.has(desc)) {
                seenDescriptions.add(desc);
                descriptions.push(desc);
            }
        }

        // Join descriptions naturally
        if (descriptions.length === 0) return '';
        if (descriptions.length === 1) return descriptions[0];
        if (descriptions.length === 2) return `${descriptions[0]} and ${descriptions[1]}`;

        // For 3+, use comma separation with 'and' before last
        const last = descriptions.pop();
        return `${descriptions.join(', ')}, and ${last}`;
    }

    // =========================================================================
    // PRODUCT DATA ACCESS
    // =========================================================================

    /**
     * Get product data from the global window object
     * @returns {Object|null} Product data object or null
     */
    function getProductData() {
        if (window.productPageData && window.productPageData.product) {
            return window.productPageData.product;
        }
        debugLog('No product data available in window.productPageData');
        return null;
    }

    /**
     * Get all variants for the current product
     * @returns {Array} Array of variant objects
     */
    function getProductVariants() {
        if (window.productPageData && window.productPageData.productVariants) {
            return window.productPageData.productVariants;
        }

        // Fallback to product.variants if available
        const product = getProductData();
        if (product && product.variants) {
            return product.variants;
        }

        debugLog('No variants available');
        return [];
    }

    /**
     * Get all media items for the current product
     * @returns {Array} Array of media objects
     */
    function getProductMedia() {
        if (window.productPageData && window.productPageData.productMedia) {
            return window.productPageData.productMedia;
        }

        debugLog('No media available');
        return [];
    }

    /**
     * Get product title
     * @returns {string} Product title or empty string
     */
    function getProductTitle() {
        const product = getProductData();
        if (product && product.title) {
            // Handle titles with pipe separators (common in Shopify)
            return product.title.split('|')[0].trim();
        }
        return '';
    }

    /**
     * Find a variant by SKU
     * @param {string} sku - SKU to search for
     * @returns {Object|null} Matching variant or null
     */
    function findVariantBySku(sku) {
        if (!sku) return null;

        const variants = getProductVariants();
        const normalizedSku = sku.toUpperCase().trim();

        const variant = variants.find(v => {
            if (!v.sku) return false;
            return v.sku.toUpperCase().trim() === normalizedSku;
        });

        if (variant) {
            debugLog('Found variant for SKU:', sku, variant);
        } else {
            debugLog('No variant found for SKU:', sku);
        }

        return variant || null;
    }

    /**
     * Find a variant by ID
     * @param {number|string} variantId - Variant ID to search for
     * @returns {Object|null} Matching variant or null
     */
    function findVariantById(variantId) {
        if (!variantId) return null;

        const variants = getProductVariants();
        return variants.find(v => v.id == variantId) || null;
    }

    /**
     * Extract shade name from variant title (removes SKU prefix)
     * @param {Object} variant - Variant object
     * @returns {string} Shade name without SKU
     */
    function extractShadeName(variant) {
        if (!variant) return '';

        const title = variant.title || '';
        const sku = variant.sku || '';

        // Remove SKU from the beginning of the title
        let shadeName = title;
        if (sku && title.toUpperCase().startsWith(sku.toUpperCase())) {
            shadeName = title.substring(sku.length).trim();
        }

        // Also try removing SKU with common separators
        const skuPatterns = [
            sku + ' ',
            sku + '-',
            sku + '_',
            sku
        ];

        for (const pattern of skuPatterns) {
            if (pattern && shadeName.toUpperCase().startsWith(pattern.toUpperCase())) {
                shadeName = shadeName.substring(pattern.length).trim();
                break;
            }
        }

        debugLog('Extracted shade name:', shadeName, 'from variant:', title);
        return shadeName;
    }

    // =========================================================================
    // ALT TEXT GENERATION
    // =========================================================================

    /**
     * Generate descriptive alt text for a product image
     *
     * WCAG Compliant Alt Text Generation:
     * - NO SKU codes in alt text (screen readers should not read internal codes)
     * - Descriptive, meaningful text that describes what the image shows
     * - Follows pattern: "{Product} in {Shade}" or "{Context} image of {Product}"
     *
     * Logic:
     * - If filename contains general keywords (lifestyle, shared, etc.):
     *   Returns: "A styled lifestyle image showcasing {product title}"
     *
     * - If filename contains SKU and position indicator:
     *   - 01/main: "{Product title} in {shade name} shade"
     *   - 02/swatch: "{Shade name} color swatch for {product title}"
     *   - 03+/alternate: "Alternate view of {product title} in {shade name}"
     *   - detail: "Close-up detail of {product title} in {shade name}"
     *   - default: "{Product title} in {shade name}"
     *
     * @param {string} imageUrl - Full URL or src of the image
     * @param {Object} options - Optional overrides
     * @param {string} options.productTitle - Override product title
     * @param {string} options.variantSku - Override SKU detection
     * @param {Object} options.variant - Direct variant object
     * @returns {string} Generated alt text
     */
    function generateImageAlt(imageUrl, options = {}) {
        const filename = extractFilename(imageUrl);
        const productTitle = options.productTitle || getProductTitle();

        if (!productTitle) {
            debugLog('Cannot generate alt: no product title available');
            // Fallback: humanize the filename (remove SKUs, separators)
            return humanizeFilename(filename) || 'LA Girl cosmetic product';
        }

        // Check for general/lifestyle images first
        if (hasGeneralKeyword(filename)) {
            const keywords = findAllGeneralKeywords(filename);
            const keywordPhrase = buildKeywordPhrase(keywords);

            // Build descriptive alt text including the keyword(s)
            // Examples:
            // - "arch-envy-brow-shared-infograph-01" -> "A stylish infographic image for Arch Envy Brow Pencil showcase its product features"
            // - "product-lifestyle-01" -> "A stylish lifestyle image showcasing Product Name"
            // - "product-shared-01" -> "A shared promotional image for Product Name"
            // - "product-tutorial-howto-01" -> "A tutorial and how-to tutorial image for Product Name"

            if (keywordPhrase) {
                // Determine article (a/an) based on first letter
                const article = /^[aeiou]/i.test(keywordPhrase) ? 'An' : 'A';

                // Special handling for video thumbnails
                if (keywords.includes('video') || keywords.includes('videos')) {
                    return `${keywordPhrase} for ${productTitle}`;
                }

                // Special handling for infographic images - add "showcase its product features"
                if (keywords.includes('infograph') || keywords.includes('infographic')) {
                    return `${article} stylish ${keywordPhrase} image for ${productTitle} showcase its product features`;
                }

                return `${article} stylish ${keywordPhrase} image for ${productTitle}`;
            }

            // Fallback if no keyword phrase built
            return `${productTitle} promotional image`;
        }

        // Extract SKU from filename or use provided override
        const skuFromFilename = options.variantSku || extractSkuFromFilename(filename);

        // Find matching variant
        let variant = options.variant || null;
        if (!variant && skuFromFilename) {
            variant = findVariantBySku(skuFromFilename);
        }

        // If we have a variant, generate detailed alt text (NO SKU in output)
        if (variant) {
            const shadeName = extractShadeName(variant);
            const imagePosition = determineImagePosition(filename);

            // Only include shade if we actually have one
            if (shadeName) {
                switch (imagePosition) {
                    case 'main':
                        return `${productTitle} in ${shadeName} shade`;

                    case 'swatch':
                        return `${shadeName} color swatch for ${productTitle}`;

                    case 'detail':
                        return `Close-up detail of ${productTitle} in ${shadeName}`;

                    case 'alternate':
                        return `Alternate view of ${productTitle} in ${shadeName}`;

                    default:
                        return `${productTitle} in ${shadeName}`;
                }
            } else {
                // No shade name available - still add context
                switch (imagePosition) {
                    case 'main':
                        return `${productTitle} product image`;
                    case 'swatch':
                        return `Color swatch for ${productTitle}`;
                    case 'detail':
                        return `Close-up detail of ${productTitle}`;
                    case 'alternate':
                        return `Alternate view of ${productTitle}`;
                    default:
                        return `${productTitle} product image`; // Always add 'product image' context
                }
            }
        }

        // Fallback: no variant match found
        const imagePosition = determineImagePosition(filename);

        switch (imagePosition) {
            case 'main':
                return `${productTitle} product image`;
            case 'swatch':
                return `Color swatch for ${productTitle}`;
            case 'detail':
                return `Close-up detail of ${productTitle}`;
            case 'alternate':
                return `Alternate view of ${productTitle}`;
            default:
                return `${productTitle} product image`; // Always add context, never just the title
        }
    }

    /**
     * Humanize a filename by removing SKUs, UUIDs, and converting to readable text
     * @param {string} filename - Filename to humanize
     * @returns {string} Human-readable string
     */
    function humanizeFilename(filename) {
        if (!filename) return '';

        let result = filename;

        // Remove UUIDs (e.g., bd4853fb-f9cc-4167-9e4d-69528880c1dc)
        result = result.replace(/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi, '');

        // Remove SKU patterns
        result = result.replace(config.skuPatterns.basic, '');

        // Remove position indicators like -01, _02
        result = result.replace(/[-_]?\d{2}$/g, '');

        // Replace separators with spaces
        result = result.replace(/[-_\.]+/g, ' ');

        // Remove extra spaces and trim
        result = result.replace(/\s+/g, ' ').trim();

        // Capitalize first letter
        if (result.length > 0) {
            result = result.charAt(0).toUpperCase() + result.slice(1);
        }

        return result;
    }

    /**
     * Generate alt text for a variant-specific image
     * @param {number|string} variantId - Variant ID
     * @param {string} imageUrl - Image URL (optional, for position detection)
     * @returns {string} Generated alt text
     */
    function generateVariantImageAlt(variantId, imageUrl = '') {
        const variant = findVariantById(variantId);

        if (!variant) {
            debugLog('Cannot generate variant alt: variant not found');
            return generateImageAlt(imageUrl);
        }

        return generateImageAlt(imageUrl, { variant });
    }

    // =========================================================================
    // DOM MANIPULATION
    // =========================================================================

    /**
     * Apply generated alt text to all product images on the page
     * Supports product pages, collection pages, and product cards
     * @param {string} selector - CSS selector for images to update (optional)
     * @param {Object} options - Additional options
     * @param {string} options.productTitle - Override product title for all images
     */
    function applyAltTextToImages(selector = null, options = {}) {
        // Comprehensive selectors for LA Girl theme
        const defaultSelectors = [
            // Product page media
            '.product-media img',
            '.product-gallery img',
            '.product__media img',
            '[data-product-media] img',
            '.product-media-gallery img',

            // Thumbnail bars (mobile and desktop)
            '.thumbnail-item-mobile img',
            '.thumbnail-item img',
            '.thumbnail-bar-container img',
            '#thumbnail-bar-container-mobile img',
            '#thumbnail-bar-container img',

            // Swiper/carousel images
            '.swiper-slide img',
            '.swiper-wrapper img',

            // Product cards (collection pages)
            '.product-card img',
            '.product-tile img',
            '.product-tile__image img',
            '.card-product img',
            '.product-grid-item img',
            '.grid-product img',
            '.grid-product__image',  // Direct image elements
            '.grid-item.product-item img',

            // Swatch images
            '.product-tile__swatches img',
            '.swatch-image img',
            '.variant-image img',

            // Featured collections
            '.featured-collection img',
            '.collection-grid img',

            // Generic product image containers
            '[data-product-image] img',
            '.product-image img',
            '.product-img img',
            '.product-image-wrapper img',
            '.grid-product__image-wrapper img',

            // Main product images
            '.product-main-image img',
            '.product-featured-image img',

            // Related products and recommendations
            '.related-product img',
            '.recommendations img',
            '.also-bought img',

            // Wishlist and cart images
            '.wishlist-item img',
            '.cart-item img',
            '.kd-product-image'
        ];

        const targetSelector = selector || defaultSelectors.join(', ');
        const images = document.querySelectorAll(targetSelector);

        debugLog('Applying alt text to', images.length, 'images');

        let updatedCount = 0;

        images.forEach((img) => {
            const currentAlt = img.getAttribute('alt');
            const src = img.getAttribute('src') || img.getAttribute('data-src') || img.getAttribute('data-srcset');

            // Try to get product context from various sources
            let productTitle = options.productTitle || getProductTitle() || null;
            let variantId = null;
            let variantSku = null;
            let filenameFromData = null;

            // Check for variant context from parent elements
            const variantContainer = img.closest('[data-variant-id]');
            if (variantContainer) {
                variantId = variantContainer.dataset.variantId;
            }

            // Check for SKU in data attributes
            const skuContainer = img.closest('[data-sku]');
            if (skuContainer) {
                variantSku = skuContainer.dataset.sku;
            }

            // Check for filename in data-name attribute (used in thumbnail containers)
            // Note: data-name may contain "filename common" format from Liquid template
            const thumbnailContainer = img.closest('[data-name]');
            if (thumbnailContainer) {
                const rawDataName = thumbnailContainer.dataset.name || '';
                // Extract just the filename part (first token before any space)
                // This handles format: "arch-envy-brow-shared-infograph-01 common"
                filenameFromData = rawDataName.trim().split(/\s+/)[0];
                debugLog('Extracted filenameFromData:', filenameFromData, 'from raw:', rawDataName);
                // Extract SKU from the data-name if we don't have one yet
                if (!variantSku && filenameFromData) {
                    variantSku = extractSkuFromFilename(filenameFromData);
                }
            }

            // Skip if already has meaningful alt text (not just product title)
            // Pass image source and filename so we can check if filename has keywords
            // that would generate better alt text than just the product title
            const needsEnhancement = isSkuOnlyAlt(currentAlt, {
                productTitle: productTitle,
                imageSrc: src,
                filenameFromData: filenameFromData
            });

            debugLog('Image check:', {
                currentAlt: currentAlt,
                filenameFromData: filenameFromData,
                src: src,
                needsEnhancement: needsEnhancement
            });

            if (currentAlt && !needsEnhancement) {
                debugLog('Skipping image with existing meaningful alt:', currentAlt);
                return;
            }

            // Try to get product title from nearby elements if not on product page
            if (!productTitle) {
                // Look for product title in card/tile structure
                const card = img.closest('.product-card, .product-tile, .card-product, .product-grid-item');
                if (card) {
                    const titleEl = card.querySelector('.product-title, .product-card__title, .product-tile__title, h3, h4');
                    if (titleEl) {
                        productTitle = titleEl.textContent.trim();
                    }
                }
            }

            // Use filename from data attribute if available, otherwise extract from src
            const effectiveSrc = filenameFromData ?
                `/${filenameFromData}.jpg` : // Create a pseudo-src for filename extraction
                src;

            // Generate new alt text
            let newAlt;
            if (variantId) {
                newAlt = generateVariantImageAlt(variantId, effectiveSrc);
            } else {
                newAlt = generateImageAlt(effectiveSrc, {
                    productTitle: productTitle,
                    variantSku: variantSku
                });
            }

            if (newAlt && newAlt !== currentAlt) {
                img.setAttribute('alt', newAlt);
                updatedCount++;
                debugLog('Applied alt text:', newAlt, 'to image:', src);
            }
        });

        debugLog(`Updated ${updatedCount} of ${images.length} images`);
        return updatedCount;
    }

    /**
     * Apply alt text specifically to product card images on collection pages
     * Handles cases where window.productPageData is not available
     */
    function applyAltToProductCards() {
        const cards = document.querySelectorAll('.product-card, .product-tile, .card-product, .product-grid-item');

        debugLog('Processing', cards.length, 'product cards');

        cards.forEach((card) => {
            // Get product title from card
            const titleEl = card.querySelector('.product-title, .product-card__title, .product-tile__title, [data-product-title], h3, h4 a');
            const productTitle = titleEl ? titleEl.textContent.trim() : null;

            if (!productTitle) {
                debugLog('No product title found for card');
                return;
            }

            // Process all images in this card
            const images = card.querySelectorAll('img');
            images.forEach((img) => {
                const currentAlt = img.getAttribute('alt');
                const src = img.getAttribute('src') || img.getAttribute('data-src');

                // Check for filename in data-name attribute
                // Note: data-name may contain "filename common" format from Liquid template
                const thumbnailContainer = img.closest('[data-name]');
                const rawDataName = thumbnailContainer?.dataset?.name || '';
                const filenameFromData = rawDataName.trim().split(/\s+/)[0] || '';

                // Pass image source and filename to properly check if alt needs enhancement
                if (currentAlt && !isSkuOnlyAlt(currentAlt, {
                    productTitle: productTitle,
                    imageSrc: src,
                    filenameFromData: filenameFromData
                })) {
                    return; // Already has meaningful alt
                }

                const variantSku = img.closest('[data-sku]')?.dataset.sku ||
                                   img.dataset.sku ||
                                   extractSkuFromFilename(extractFilename(src));

                const newAlt = generateImageAlt(src, {
                    productTitle: productTitle,
                    variantSku: variantSku
                });

                if (newAlt) {
                    img.setAttribute('alt', newAlt);
                }
            });
        });
    }

    /**
     * Check if an alt text is meaningless or needs enhancement
     * Based on WCAG 2.2 Issues #1 and #2 from accessibility report:
     * - F30: Alt text should not be meaningless image file names
     * - F30: Alt text should not contain placeholders like 'picture' or 'spacer'
     *
     * PRIORITY ORDER:
     * 1. FIRST check if filename has specific keywords (infograph, lifestyle, etc.)
     *    - If filename has keywords, alt ALWAYS needs enhancement regardless of current alt text
     * 2. Then check for empty/meaningless alt text
     * 3. SKU-only checks at the end as fallback
     *
     * @param {string} altText - Alt text to check
     * @param {Object} options - Additional context
     * @param {string} options.productTitle - Product title to compare against
     * @param {string} options.imageSrc - Image source URL for context
     * @param {string} options.filenameFromData - Filename from data attribute (e.g., data-name)
     * @returns {boolean} True if alt is meaningless and should be replaced/enhanced
     */
    function isSkuOnlyAlt(altText, options = {}) {
        // =========================================================================
        // PRIORITY 1: Check filename keywords FIRST
        // If filename has specific keywords like 'infograph', 'lifestyle', 'shared',
        // the alt text should ALWAYS be enhanced with this context, regardless of
        // what the current alt text is (unless it already has the enhanced format)
        // =========================================================================
        const imageSrc = options.imageSrc || '';
        const filenameFromData = options.filenameFromData || '';
        const filename = filenameFromData || extractFilename(imageSrc);

        if (filename && hasGeneralKeyword(filename)) {
            // Check if current alt text already has descriptive context from keywords
            const trimmedAlt = (altText || '').trim().toLowerCase();
            const descriptiveKeywordPhrases = [
                'infographic', 'lifestyle', 'tutorial', 'promotional',
                'showcase', 'stylish', 'featuring', 'video thumbnail',
                'how-to', 'before application', 'after application',
                'color swatch', 'model showcase', 'inspiration'
            ];

            const alreadyHasKeywordContext = descriptiveKeywordPhrases.some(phrase =>
                trimmedAlt.includes(phrase)
            );

            if (!alreadyHasKeywordContext) {
                debugLog('Filename has general keywords, alt needs enhancement:', filename);
                return true; // Needs enhancement with keyword context
            }
        }

        // =========================================================================
        // PRIORITY 2: Check for empty or missing alt text
        // =========================================================================
        if (!altText || altText.trim() === '') return true;

        const trimmed = altText.trim();
        const lowerTrimmed = trimmed.toLowerCase();

        // =========================================================================
        // PRIORITY 3: Check for placeholder/meaningless alt text patterns
        // =========================================================================

        // Common placeholder patterns identified in WCAG report
        const placeholders = [
            'image',
            'img',
            'photo',
            'picture',
            'product',
            'spacer',
            'placeholder',
            'untitled',
            'no image',
            'noimage',
            'default',
            'thumbnail',
            'thumb',
            'pic',
            'graphic',
            'banner',
            'icon'
        ];

        if (placeholders.includes(lowerTrimmed)) {
            return true;
        }

        // Check for filename-like patterns (contains file extension or underscore patterns)
        // e.g., "product_image_01.jpg" or "IMG_1234"
        const filenamePatterns = [
            /\.(jpg|jpeg|png|gif|webp|svg)$/i,  // Has file extension
            /^img[-_]?\d+$/i,                    // IMG_1234 pattern
            /^dsc[-_]?\d+$/i,                    // DSC_1234 (camera default)
            /^image[-_]?\d+$/i,                  // image_01 pattern
            /^photo[-_]?\d+$/i,                  // photo_01 pattern
            /^pic[-_]?\d+$/i,                    // pic_01 pattern
            /^\d+$/,                              // Just numbers
            /^[a-f0-9]{8}-[a-f0-9]{4}/i          // UUID pattern
        ];

        for (const pattern of filenamePatterns) {
            if (pattern.test(lowerTrimmed)) {
                return true;
            }
        }

        // Check if alt is just a color code (hex)
        if (/^#?[a-f0-9]{3,6}$/i.test(trimmed)) {
            return true;
        }

        // Check for very short meaningless text (1-2 characters)
        if (trimmed.length <= 2) {
            return true;
        }

        // =========================================================================
        // PRIORITY 4: Check if alt matches product title (needs enhancement)
        // =========================================================================
        const productTitle = options.productTitle || getProductTitle();
        if (productTitle) {
            const normalizedAlt = lowerTrimmed;
            const normalizedTitle = productTitle.toLowerCase().trim();

            // If alt exactly matches product title, it needs enhancement
            const altMatchesTitle = normalizedAlt === normalizedTitle ||
                normalizedAlt === normalizedTitle.replace(/\s*\|.*$/, '').trim() ||
                normalizedAlt === normalizedTitle.split('|')[0].trim().toLowerCase();

            if (altMatchesTitle) {
                // Check if filename has additional context we can add
                if (filename) {
                    // Check if filename has SKU that could provide shade/variant info
                    const skuFromFilename = extractSkuFromFilename(filename);
                    if (skuFromFilename) {
                        const variant = findVariantBySku(skuFromFilename);
                        if (variant) {
                            const shadeName = extractShadeName(variant);
                            if (shadeName) {
                                debugLog('Alt matches product title but filename has SKU with shade:', shadeName);
                                return true; // Needs enhancement with shade info
                            }
                        }
                    }

                    // Check if filename has position indicators (main, swatch, detail, alternate)
                    const imagePosition = determineImagePosition(filename);
                    if (imagePosition !== 'product') {
                        debugLog('Alt matches product title but filename indicates position:', imagePosition);
                        return true; // Needs enhancement with position context
                    }
                }

                // Alt matches title but no additional filename info
                // Still mark as needing enhancement to add at least "product image" context
                return true;
            }
        }

        // =========================================================================
        // PRIORITY 5 (LAST): Check for generic product name patterns
        // =========================================================================

        // If the alt text is very short (under 30 chars) and consists only of
        // what looks like a product name, it probably needs enhancement
        if (trimmed.length < 30) {
            // Check if it looks like a product name without context
            // Product names typically don't include descriptive words
            const descriptiveWords = [
                'image', 'view', 'showing', 'displaying', 'featuring',
                'swatch', 'color', 'shade', 'alternate', 'detail',
                'close-up', 'product', 'promotional', 'lifestyle',
                'tutorial', 'video', 'infographic', 'styled', 'stylish',
                'for', 'of', 'in', 'showcase'  // Prepositions that indicate descriptive context
            ];

            const hasDescriptiveContext = descriptiveWords.some(word =>
                lowerTrimmed.includes(word)
            );

            // If it's short and has no descriptive context, likely needs enhancement
            if (!hasDescriptiveContext && trimmed.length < 40) {
                // Only if it looks like just a product/brand name
                // Check if every word could be a product name (capitalized, brand-like)
                const words = trimmed.split(/\s+/);
                const allProductNameLike = words.every(word =>
                    // Product name words are typically short or have specific patterns
                    word.length <= 15 && /^[A-Za-z0-9.'"-]+$/.test(word)
                );

                if (allProductNameLike && words.length <= 6) {
                    // Likely just a product name like "HD Pro.Conceal" - needs context
                    return true;
                }
            }
        }

        // =========================================================================
        // FALLBACK: Check SKU-only patterns at the very end
        // =========================================================================

        // Check if it matches SKU pattern only (e.g., "GLM711", "GES94")
        const skuMatch = trimmed.match(config.skuPatterns.basic);
        if (skuMatch && skuMatch[0].length === trimmed.length) {
            return true;
        }

        // Check if alt contains ONLY a SKU (with possible whitespace)
        const skuOnlyPattern = /^[A-Z]{2,5}\d{2,5}[A-Z0-9_-]*$/i;
        if (skuOnlyPattern.test(trimmed)) {
            return true;
        }

        return false;
    }

    /**
     * Check if alt text can be enhanced (has product title but missing variant info)
     * @param {string} altText - Current alt text
     * @param {string} imageSrc - Image source URL
     * @returns {boolean} True if alt can be enhanced with more detail
     */
    function canEnhanceAlt(altText, imageSrc) {
        if (!altText || !imageSrc) return false;

        const productTitle = getProductTitle();
        if (!productTitle) return false;

        const normalizedAlt = altText.trim().toLowerCase();
        const normalizedTitle = productTitle.toLowerCase().trim();

        // If current alt is just the product title
        if (normalizedAlt === normalizedTitle || normalizedAlt === normalizedTitle.split('|')[0].trim().toLowerCase()) {
            // Check if the image filename has SKU or position info we can use
            const filename = extractFilename(imageSrc);
            const sku = extractSkuFromFilename(filename);
            const positionType = determineImagePosition(filename);
            const isGeneralImage = hasGeneralKeyword(filename);

            return sku || positionType !== 'product' || isGeneralImage;
        }

        return false;
    }

    /**
     * Observe DOM for dynamically added images and apply alt text
     * @param {string} containerSelector - Selector for container to observe
     */
    function observeAndApplyAlt(containerSelector = 'body') {
        const container = document.querySelector(containerSelector);
        if (!container) return;

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        // Check if the added node is an image
                        if (node.tagName === 'IMG') {
                            const src = node.getAttribute('src') || node.getAttribute('data-src');
                            const thumbnailContainer = node.closest?.('[data-name]');
                            // Note: data-name may contain "filename common" format
                            const rawDataName = thumbnailContainer?.dataset?.name || '';
                            const filenameFromData = rawDataName.trim().split(/\s+/)[0] || '';

                            if (src && isSkuOnlyAlt(node.getAttribute('alt'), {
                                imageSrc: src,
                                filenameFromData: filenameFromData
                            })) {
                                const newAlt = generateImageAlt(src);
                                if (newAlt) {
                                    node.setAttribute('alt', newAlt);
                                }
                            }
                        }

                        // Check for images within the added node
                        const images = node.querySelectorAll?.('img');
                        if (images) {
                            images.forEach((img) => {
                                const src = img.getAttribute('src') || img.getAttribute('data-src');
                                const thumbnailContainer = img.closest('[data-name]');
                                // Note: data-name may contain "filename common" format
                                const rawDataName = thumbnailContainer?.dataset?.name || '';
                                const filenameFromData = rawDataName.trim().split(/\s+/)[0] || '';

                                if (src && isSkuOnlyAlt(img.getAttribute('alt'), {
                                    imageSrc: src,
                                    filenameFromData: filenameFromData
                                })) {
                                    const newAlt = generateImageAlt(src);
                                    if (newAlt) {
                                        img.setAttribute('alt', newAlt);
                                    }
                                }
                            });
                        }
                    }
                });
            });
        });

        observer.observe(container, {
            childList: true,
            subtree: true
        });

        debugLog('MutationObserver started for:', containerSelector);
        return observer;
    }

    // =========================================================================
    // WCAG ACCESSIBILITY FIXES (Based on Accessibility Report)
    // =========================================================================

    /**
     * Fix empty headings by hiding them from accessibility tree or adding content
     * WCAG Issue: "Headings should not be empty."
     * ARIA 1.2 5.2.1
     */
    function fixEmptyHeadings() {
        const selectors = config.emptyHeadingSelectors.join(', ');
        let fixedCount = 0;

        try {
            const emptyHeadings = document.querySelectorAll(selectors);
            emptyHeadings.forEach((heading) => {
                // Check if truly empty (no text content, no meaningful children)
                if (!heading.textContent.trim() && !heading.querySelector('img[alt]:not([alt=""])')) {
                    // Hide from accessibility tree while preserving any visual styling
                    heading.setAttribute('aria-hidden', 'true');
                    heading.setAttribute('role', 'presentation');
                    fixedCount++;
                    debugLog('Fixed empty heading:', heading);
                }
            });
        } catch (e) {
            debugLog('Error fixing empty headings:', e);
        }

        debugLog(`Fixed ${fixedCount} empty headings`);
        return fixedCount;
    }

    /**
     * Fix elements with role=button that have no accessible name
     * WCAG Issue: "ARIA role=button element contains no text and has no accessible name."
     * WCAG 2.2 A 4.1.2
     */
    function fixButtonRoleAccessibleNames() {
        let fixedCount = 0;

        try {
            // Fix .acsb-trigger buttons (accessibility widget trigger)
            const acsbTriggers = document.querySelectorAll('.acsb-trigger[role="button"]:not([aria-label])');
            acsbTriggers.forEach((trigger) => {
                trigger.setAttribute('aria-label', 'Accessibility options');
                fixedCount++;
                debugLog('Fixed acsb-trigger button:', trigger);
            });

            // Fix other role=button elements without accessible names
            const roleButtons = document.querySelectorAll('[role="button"]:not([aria-label]):not([aria-labelledby])');
            roleButtons.forEach((button) => {
                const text = button.textContent.trim();
                if (!text) {
                    // Try to get context from nearby elements
                    const parent = button.closest('[data-product-title], .product-card, .product-tile');
                    const productTitle = parent?.querySelector('.product-title, [data-product-title]')?.textContent?.trim();

                    // Check for images within
                    const img = button.querySelector('img');
                    const imgAlt = img?.getAttribute('alt');

                    // Check for video indicators
                    const hasVideo = button.querySelector('.media-play-icon, [class*="play"]');

                    if (hasVideo && productTitle) {
                        button.setAttribute('aria-label', `Play video for ${productTitle}`);
                    } else if (imgAlt && imgAlt.trim()) {
                        button.setAttribute('aria-label', imgAlt);
                    } else if (productTitle) {
                        button.setAttribute('aria-label', `View ${productTitle}`);
                    } else {
                        button.setAttribute('aria-label', 'Interactive element');
                    }
                    fixedCount++;
                    debugLog('Fixed role=button element:', button);
                }
            });
        } catch (e) {
            debugLog('Error fixing button role accessible names:', e);
        }

        debugLog(`Fixed ${fixedCount} button role elements`);
        return fixedCount;
    }

    /**
     * Fix blank labels by adding descriptive text
     * WCAG Issue: "The label element is blank."
     * WCAG 2.2 A 4.1.2
     */
    function fixBlankLabels() {
        let fixedCount = 0;

        try {
            // Fix mobile-drawer labels
            const mobileDrawerLabels = document.querySelectorAll('label[for="mobile-drawer"]');
            mobileDrawerLabels.forEach((label) => {
                if (!label.textContent.trim() && !label.getAttribute('aria-label')) {
                    label.setAttribute('aria-label', 'Toggle mobile navigation menu');
                    fixedCount++;
                    debugLog('Fixed mobile-drawer label:', label);
                }
            });

            // Fix product swatch labels
            // AS-01: the card swatch is a <button> now and carries its own visually hidden
            // name, so match on the class and skip anything already named. Without the skip
            // this would overwrite the markup's name with a second, competing one.
            const swatchLabels = document.querySelectorAll('.product-tile__swatch:not([aria-label])');
            swatchLabels.forEach((label) => {
                if (label.textContent.trim()) return;
                const swatchColor = label.dataset.swatchColor || label.dataset.title || '';
                const variantTitle = label.dataset.title || label.dataset.variantTitle || '';

                if (variantTitle) {
                    label.setAttribute('aria-label', `Select color: ${variantTitle}`);
                } else if (swatchColor) {
                    // Convert kebab-case to readable text (e.g., "gc969-porcelain" -> "Porcelain")
                    const colorName = swatchColor.replace(/^[a-z]+-/, '').replace(/-/g, ' ');
                    label.setAttribute('aria-label', `Select color: ${colorName}`);
                } else {
                    label.setAttribute('aria-label', 'Select color option');
                }
                fixedCount++;
                debugLog('Fixed swatch label:', label);
            });

            // Fix any other empty label-like elements
            const emptyLabels = document.querySelectorAll('label:not([aria-label])');
            emptyLabels.forEach((label) => {
                if (!label.textContent.trim() && !label.querySelector('img[alt]')) {
                    const forAttr = label.getAttribute('for');
                    if (forAttr) {
                        const input = document.getElementById(forAttr);
                        if (input) {
                            const placeholder = input.getAttribute('placeholder');
                            const name = input.getAttribute('name');
                            if (placeholder) {
                                label.setAttribute('aria-label', placeholder);
                                fixedCount++;
                            } else if (name) {
                                label.setAttribute('aria-label', name.replace(/[\[\]_-]/g, ' ').trim());
                                fixedCount++;
                            }
                        }
                    }
                }
            });
        } catch (e) {
            debugLog('Error fixing blank labels:', e);
        }

        debugLog(`Fixed ${fixedCount} blank labels`);
        return fixedCount;
    }

    /**
     * Fix broken skip links by creating target IDs if they don't exist
     * WCAG Issue: "This skip link is broken. The target anchor does not exist."
     * WCAG 2.2 A 2.4.1
     */
    function fixBrokenSkipLinks() {
        let fixedCount = 0;

        try {
            for (const [targetId, fallbackSelector] of Object.entries(config.skipLinkTargets)) {
                // Check if the target ID already exists
                if (!document.getElementById(targetId)) {
                    // Find the best matching element using fallback selectors
                    const fallbackElement = document.querySelector(fallbackSelector);
                    if (fallbackElement) {
                        // Don't override an existing ID
                        if (!fallbackElement.id) {
                            fallbackElement.id = targetId;
                            fixedCount++;
                            debugLog(`Created skip link target #${targetId} on:`, fallbackElement);
                        } else {
                            // Element has different ID, update the skip link href instead
                            const skipLink = document.querySelector(`a[href="#${targetId}"]`);
                            if (skipLink) {
                                skipLink.href = `#${fallbackElement.id}`;
                                fixedCount++;
                                debugLog(`Updated skip link to point to #${fallbackElement.id}`);
                            }
                        }
                    } else {
                        debugLog(`No fallback element found for skip link target #${targetId}`);
                    }
                }
            }
        } catch (e) {
            debugLog('Error fixing broken skip links:', e);
        }

        debugLog(`Fixed ${fixedCount} broken skip links`);
        return fixedCount;
    }

    /**
     * Fix links without accessible names
     * WCAG Issue: "Links must have an accessible name."
     * WCAG 2.2 A F89
     */
    function fixLinksWithoutAccessibleNames() {
        let fixedCount = 0;

        try {
            // Fix links that only contain pictures without alt text
            const pictureLinks = document.querySelectorAll('a:has(picture):not([aria-label])');
            pictureLinks.forEach((link) => {
                const img = link.querySelector('img');
                const hasAltText = img?.getAttribute('alt')?.trim();

                if (!hasAltText && !link.textContent.trim()) {
                    // Try to derive context from href or nearby elements
                    const href = link.getAttribute('href') || '';
                    const parent = link.closest('.product-card, .product-tile, [data-product-title]');
                    const productTitle = parent?.querySelector('.product-title, [data-product-title]')?.textContent?.trim();

                    if (productTitle) {
                        link.setAttribute('aria-label', `View ${productTitle}`);
                    } else if (href.includes('/products/')) {
                        const productHandle = href.split('/products/')[1]?.split('?')[0] || '';
                        const readableName = productHandle.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                        link.setAttribute('aria-label', `View ${readableName || 'product'}`);
                    } else if (href.includes('/collections/')) {
                        const collectionHandle = href.split('/collections/')[1]?.split('?')[0] || '';
                        const readableName = collectionHandle.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                        link.setAttribute('aria-label', `Browse ${readableName || 'collection'}`);
                    } else {
                        link.setAttribute('aria-label', 'View image');
                    }
                    fixedCount++;
                    debugLog('Fixed link without accessible name:', link);
                }
            });

            // Fix icon links (social media, etc.)
            const iconLinks = document.querySelectorAll('a.icon-class:not([aria-label])');
            iconLinks.forEach((link) => {
                const href = link.getAttribute('href') || '';
                let label = 'External link';

                // Determine social network from URL
                if (href.includes('instagram')) label = 'Instagram';
                else if (href.includes('facebook')) label = 'Facebook';
                else if (href.includes('twitter') || href.includes('x.com')) label = 'X (Twitter)';
                else if (href.includes('youtube')) label = 'YouTube';
                else if (href.includes('tiktok')) label = 'TikTok';
                else if (href.includes('pinterest')) label = 'Pinterest';
                else if (href.includes('linkedin')) label = 'LinkedIn';

                if (link.getAttribute('target') === '_blank') {
                    label += ' (opens in new window)';
                }

                link.setAttribute('aria-label', label);
                fixedCount++;
                debugLog('Fixed icon link:', link);
            });

            // NEW: Fix .post-video links (video links without accessible names)
            // WCAG Issue: Links with only images inside need accessible names
            const videoLinks = document.querySelectorAll('a.post-video:not([aria-label]), a#y-link:not([aria-label])');
            videoLinks.forEach((link) => {
                const img = link.querySelector('img');
                const videoId = img?.getAttribute('data-video-id') || link.getAttribute('href')?.match(/youtube\.com\/embed\/([^?]+)/)?.[1] || '';
                
                // Get context from parent blog/article structure
                const article = link.closest('article, .blog-post, .event-post, [class*="blog"], [class*="event"]');
                const articleTitle = article?.querySelector('h1, h2, h3, .post-title, .article-title')?.textContent?.trim();
                
                if (articleTitle) {
                    link.setAttribute('aria-label', `Play video: ${articleTitle}`);
                } else if (videoId) {
                    link.setAttribute('aria-label', `Play video content`);
                } else {
                    link.setAttribute('aria-label', 'Play video');
                }
                
                // Also fix the image alt if missing
                if (img && !img.getAttribute('alt')) {
                    img.setAttribute('alt', articleTitle ? `Video thumbnail for ${articleTitle}` : 'Video thumbnail');
                }
                
                fixedCount++;
                debugLog('Fixed video link:', link);
            });

            // NEW: Fix links containing only images without alt text
            const imgOnlyLinks = document.querySelectorAll('a:has(img):not([aria-label])');
            imgOnlyLinks.forEach((link) => {
                const img = link.querySelector('img');
                const imgAlt = img?.getAttribute('alt')?.trim();
                const linkText = link.textContent.trim();
                
                // Only fix if there's no accessible name source
                if (!imgAlt && !linkText) {
                    const href = link.getAttribute('href') || '';
                    
                    // Try to extract meaningful context
                    if (href.includes('youtube') || href.includes('vimeo')) {
                        link.setAttribute('aria-label', 'Play video');
                    } else if (href.includes('/products/')) {
                        const handle = href.split('/products/')[1]?.split('?')[0] || '';
                        const name = handle.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                        link.setAttribute('aria-label', `View ${name || 'product'}`);
                    } else if (href.includes('/blogs/') || href.includes('/news/') || href.includes('/events/')) {
                        link.setAttribute('aria-label', 'Read article');
                    } else {
                        link.setAttribute('aria-label', 'View content');
                    }
                    fixedCount++;
                    debugLog('Fixed image-only link:', link);
                }
            });

        } catch (e) {
            debugLog('Error fixing links without accessible names:', e);
        }

        debugLog(`Fixed ${fixedCount} links without accessible names`);
        return fixedCount;
    }

    /**
     * Fix duplicate link text by adding unique aria-labels
     * WCAG Issue: "Several links share the same link text but go to different destinations"
     * WCAG 2.2 A F63
     */
    function fixDuplicateLinkText() {
        let fixedCount = 0;

        try {
            // Group links by their visible text
            const linksByText = new Map();
            const allLinks = document.querySelectorAll('a:not([aria-label])');
            
            allLinks.forEach((link) => {
                const text = link.textContent.trim().toLowerCase();
                if (text && text.length > 0 && text.length < 50) {
                    if (!linksByText.has(text)) {
                        linksByText.set(text, []);
                    }
                    linksByText.get(text).push(link);
                }
            });

            // Fix links with duplicate text that go to different destinations
            linksByText.forEach((links, text) => {
                if (links.length > 1) {
                    // Get unique href values
                    const hrefSet = new Set(links.map(l => l.getAttribute('href')));
                    
                    // Only fix if same text links to different destinations
                    if (hrefSet.size > 1) {
                        links.forEach((link) => {
                            const href = link.getAttribute('href') || '';
                            let context = '';
                            
                            // Extract context from href
                            if (href.includes('/products/')) {
                                const handle = href.split('/products/')[1]?.split('?')[0] || '';
                                context = handle.replace(/-/g, ' ');
                            } else if (href.includes('/collections/')) {
                                const handle = href.split('/collections/')[1]?.split('?')[0] || '';
                                context = handle.replace(/-/g, ' ');
                            } else if (href.includes('/blogs/')) {
                                const pathParts = href.split('/');
                                const blogCategory = pathParts[pathParts.indexOf('blogs') + 1] || '';
                                context = blogCategory.replace(/-/g, ' ');
                            } else if (href.includes('/pages/')) {
                                const handle = href.split('/pages/')[1]?.split('?')[0] || '';
                                context = handle.replace(/-/g, ' ');
                            }
                            
                            // Get context from nearby product/article
                            if (!context) {
                                const parent = link.closest('.product-card, .product-tile, article, [class*="product"]');
                                const titleEl = parent?.querySelector('h2, h3, .product-title, .title');
                                context = titleEl?.textContent?.trim() || '';
                            }
                            
                            if (context) {
                                // Capitalize context
                                context = context.replace(/\b\w/g, l => l.toUpperCase());
                                const originalText = link.textContent.trim();
                                link.setAttribute('aria-label', `${originalText} - ${context}`);
                                fixedCount++;
                                debugLog('Fixed duplicate link text:', link, `Added context: ${context}`);
                            }
                        });
                    }
                }
            });

            // Special handling for rating links (e.g., "0.0 out of 5 stars")
            const ratingLinks = document.querySelectorAll('a[class*="review"], a[class*="rating"], a:has([class*="star"])');
            ratingLinks.forEach((link) => {
                if (!link.getAttribute('aria-label')) {
                    // Get product context
                    const product = link.closest('.product-card, .product-tile, [class*="product"]');
                    const productTitle = product?.querySelector('.product-title, h2, h3')?.textContent?.trim();
                    
                    if (productTitle) {
                        const ratingText = link.textContent.trim() || 'View reviews';
                        link.setAttribute('aria-label', `${ratingText} for ${productTitle}`);
                        fixedCount++;
                        debugLog('Fixed rating link:', link);
                    }
                }
            });

        } catch (e) {
            debugLog('Error fixing duplicate link text:', e);
        }

        debugLog(`Fixed ${fixedCount} duplicate link text issues`);
        return fixedCount;
    }

    /**
     * Name Bazaarvoice inline-rating links after their product
     * WCAG Issue: every card's rating link reads "4.9 out of 5 stars. 96 reviews"
     * and goes to a different product (SS-ACC-0923-04, WCAG 2.2 A 2.4.4 F63).
     * Bazaarvoice injects the link after DOMContentLoaded and sets its own
     * aria-label, so fixDuplicateLinkText never sees it. This appends the card's
     * product title, is idempotent, and is re-run by observeInlineRatings().
     * @returns {number} Number of links named
     */
    function nameInlineRatingLinks() {
        let fixedCount = 0;
        const TITLE_SELECTOR = '.card__title, .grid-product__title, .product-title';
        const MAX_ANCESTOR_DEPTH = 6;

        document.querySelectorAll('[data-bv-show="inline_rating"] a').forEach((link) => {
            const container = link.closest('[data-bv-show="inline_rating"]');
            let ancestor = container.parentElement;
            let titleEl = null;
            for (let i = 0; i < MAX_ANCESTOR_DEPTH && ancestor && !titleEl; i++) {
                titleEl = ancestor.querySelector(TITLE_SELECTOR);
                ancestor = ancestor.parentElement;
            }
            if (!titleEl) return;

            const titleClone = titleEl.cloneNode(true);
            titleClone.querySelectorAll('.sr-only').forEach((el) => el.remove());
            const title = titleClone.textContent.replace(/\s+/g, ' ').trim();
            if (!title) return;

            const base = (link.getAttribute('aria-label') || link.textContent)
                .replace(/\s+/g, ' ').trim() || 'Reviews';
            if (base.includes(title)) return;

            link.setAttribute('aria-label', `${base} for ${title}`);
            fixedCount++;
        });

        debugLog(`Named ${fixedCount} inline rating links`);
        return fixedCount;
    }

    /**
     * Re-run nameInlineRatingLinks whenever Bazaarvoice injects or re-renders
     * an inline rating. The rename is idempotent, so its own attribute write
     * does not loop.
     */
    function observeInlineRatings() {
        const containers = document.querySelectorAll('[data-bv-show="inline_rating"]');
        if (!containers.length) return null;

        const observer = new MutationObserver(() => nameInlineRatingLinks());
        containers.forEach((container) => {
            observer.observe(container, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['aria-label']
            });
        });
        return observer;
    }

    /**
     * Fix nested interactive controls by restructuring or fixing ARIA
     * WCAG Issue: Interactive controls nested inside other interactive controls
     * WCAG 2.2 A 4.1.2
     */
    function fixNestedInteractiveControls() {
        let fixedCount = 0;

        try {
            // Fix buttons inside links (a > button)
            const buttonsInLinks = document.querySelectorAll('a button');
            buttonsInLinks.forEach((button) => {
                // AS-07: do NOT set role="presentation" here. A <button> is focusable and
                // ARIA forbids a presentational role on a focusable element, so that added
                // two violations instead of removing one. aria-hidden is wrong too: this
                // button carries the link's visible text, so hiding it would leave the link
                // nameless. Taking it out of the tab order leaves the wrapping link as the
                // single control and keeps its accessible name intact. The real fix is the
                // article HTML (SortSite SS-ACC-002), which is a merchant-content edit.
                button.setAttribute('tabindex', '-1');
                button.removeAttribute('role');
                fixedCount++;
                debugLog('Fixed button inside link:', button);
            });

            // Fix links inside role=button elements
            const linksInButtonRole = document.querySelectorAll('[role="button"] a.glightbox');
            linksInButtonRole.forEach((link) => {
                // The link functionality is duplicated, make it non-interactive
                link.setAttribute('tabindex', '-1');
                link.setAttribute('aria-hidden', 'true');
                fixedCount++;
                debugLog('Fixed link inside role=button:', link);
            });

            // Fix labels inside buttons
            const labelsInButtons = document.querySelectorAll('button label');
            labelsInButtons.forEach((label) => {
                // Convert label to span for semantic correctness
                label.setAttribute('role', 'presentation');
                fixedCount++;
                debugLog('Fixed label inside button:', label);
            });

            // Fix buttons inside role=button
            const buttonsInRoleButton = document.querySelectorAll('[role="button"] button');
            buttonsInRoleButton.forEach((button) => {
                // AS-07: same reasoning as above.
                button.setAttribute('tabindex', '-1');
                button.removeAttribute('role');
                fixedCount++;
                debugLog('Fixed button inside role=button:', button);
            });
        } catch (e) {
            debugLog('Error fixing nested interactive controls:', e);
        }

        debugLog(`Fixed ${fixedCount} nested interactive controls`);
        return fixedCount;
    }

    /**
     * Add warnings to external links opening in new windows
     * WCAG Issue: "Avoid specifying a new window as the target of a link"
     * WCAG 2.2 AAA F22
     */
    function fixExternalLinkWarnings() {
        let fixedCount = 0;

        try {
            // Create a hidden element to be referenced by aria-describedby
            let warningElement = document.getElementById('external-link-warning');
            if (!warningElement) {
                warningElement = document.createElement('span');
                warningElement.id = 'external-link-warning';
                warningElement.className = 'sr-only visually-hidden';
                warningElement.style.cssText = 'position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;';
                warningElement.textContent = 'Opens in a new window';
                // AS-04: append inside the main landmark, not to <body>. A node parented to
                // <body> sits outside every landmark, which axe reports as content not
                // contained by a landmark. It is a describedby target, so where it lives
                // changes nothing a user perceives.
                (document.getElementById('site-main') || document.querySelector('main') || document.body)
                    .appendChild(warningElement);
            }

            const externalLinks = document.querySelectorAll('a[target="_blank"]:not([aria-describedby])');
            const ALREADY_WARNED = /new (tab|window)/i;
            externalLinks.forEach((link) => {
                // SS-ACC-0923-16: links that carry their own warning in the markup
                // (an sr-only span or an aria-label) must not be described twice.
                if (ALREADY_WARNED.test(`${link.textContent} ${link.getAttribute('aria-label') || ''}`)) {
                    return;
                }

                // Add the warning reference
                link.setAttribute('aria-describedby', 'external-link-warning');

                // Also add a visual indicator if not already present
                const hasIcon = link.querySelector('[class*="external"], [class*="new-window"]');
                if (!hasIcon) {
                    // Enhance aria-label if it exists
                    const currentLabel = link.getAttribute('aria-label');
                    if (currentLabel && !currentLabel.includes('new window')) {
                        link.setAttribute('aria-label', `${currentLabel} (opens in new window)`);
                    }
                }
                fixedCount++;
                debugLog('Fixed external link warning:', link);
            });
        } catch (e) {
            debugLog('Error fixing external link warnings:', e);
        }

        debugLog(`Fixed ${fixedCount} external link warnings`);
        return fixedCount;
    }

    /**
     * Fix form inputs without accessible labels
     * WCAG Issue: "HTML form control has no accessible name."
     * WCAG 2.2 A F68
     */
    function fixFormInputLabels() {
        let fixedCount = 0;

        try {
            const inputs = document.querySelectorAll('input:not([aria-label]):not([aria-labelledby])');
            inputs.forEach((input) => {
                // Skip hidden and submit/button types
                if (input.type === 'hidden' || input.type === 'submit' || input.type === 'button') return;

                const inputId = input.id;
                const hasLabel = inputId && document.querySelector(`label[for="${inputId}"]`);

                if (!hasLabel) {
                    // Get accessible name from various sources
                    const placeholder = input.getAttribute('placeholder');
                    const name = input.getAttribute('name');
                    const title = input.getAttribute('title');

                    if (title) {
                        // Title is already an accessible name source, leave it
                        return;
                    } else if (placeholder) {
                        input.setAttribute('aria-label', placeholder);
                        fixedCount++;
                    } else if (name) {
                        // Parse name like "contact[first name]" -> "First name"
                        let label = name.replace(/\[|\]/g, ' ').replace(/contact\s*/gi, '').trim();
                        label = label.charAt(0).toUpperCase() + label.slice(1);
                        input.setAttribute('aria-label', label);
                        fixedCount++;
                    }
                    debugLog('Fixed form input label:', input);
                }
            });

            // Also fix textareas and selects
            const otherInputs = document.querySelectorAll('textarea:not([aria-label]):not([aria-labelledby]), select:not([aria-label]):not([aria-labelledby])');
            otherInputs.forEach((input) => {
                const inputId = input.id;
                const hasLabel = inputId && document.querySelector(`label[for="${inputId}"]`);

                if (!hasLabel) {
                    const placeholder = input.getAttribute('placeholder');
                    const name = input.getAttribute('name');
                    const title = input.getAttribute('title');

                    if (!title) {
                        if (placeholder) {
                            input.setAttribute('aria-label', placeholder);
                            fixedCount++;
                        } else if (name) {
                            let label = name.replace(/\[|\]/g, ' ').replace(/contact\s*/gi, '').trim();
                            label = label.charAt(0).toUpperCase() + label.slice(1);
                            input.setAttribute('aria-label', label);
                            fixedCount++;
                        }
                    }
                    debugLog('Fixed textarea/select label:', input);
                }
            });
        } catch (e) {
            debugLog('Error fixing form input labels:', e);
        }

        debugLog(`Fixed ${fixedCount} form input labels`);
        return fixedCount;
    }

    /**
     * Enhance focus visibility for keyboard navigation
     * WCAG Issue: "CSS outline or border style makes it difficult to see the link focus outline"
     * WCAG 2.2 AA F78
     */
    /**
     * Track the input modality so the keyboard focus ring can be gated on it.
     *
     * WHY: browsers match `:focus-visible` on a TEXT FIELD even when the user
     * reached it with a mouse — the spec assumes a field about to receive typing
     * always wants a ring. That is why clicking the distributor search box lit up
     * the 3px blue ring. Buttons and links do not have this behaviour; they only
     * match :focus-visible for keyboard focus, so they are left alone.
     *
     * Setting `data-lag-keyboard-nav` on <html> on the first Tab / arrow keypress
     * and clearing it on pointerdown lets the field ring follow the KEYBOARD, not
     * the pointer. WCAG 2.4.7 Focus Visible is about keyboard operation, so this
     * keeps the theme conformant: tab to the field and the ring is there.
     */
    function trackKeyboardModality() {
        if (window.__lagKeyboardModality) return;
        window.__lagKeyboardModality = true;
        const root = document.documentElement;
        const KEYS = ['Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'];
        document.addEventListener('keydown', (e) => {
            if (KEYS.indexOf(e.key) !== -1) root.setAttribute('data-lag-keyboard-nav', '');
        }, true);
        document.addEventListener('pointerdown', () => {
            root.removeAttribute('data-lag-keyboard-nav');
        }, true);
    }

    function enhanceFocusVisibility() {
        let fixedCount = 0;

        trackKeyboardModality();

        try {
            // Create focus enhancement styles if not already present
            let styleElement = document.getElementById('lag-accessibility-focus-styles');
            if (!styleElement) {
                styleElement = document.createElement('style');
                styleElement.id = 'lag-accessibility-focus-styles';
                styleElement.textContent = `
                    /* LAG Accessibility: Enhanced Focus Visibility (WCAG 2.2 AA F78) */
                    /* Form fields are excluded here and handled by the gated rule
                       further down — see trackKeyboardModality(). Everything else
                       only matches :focus-visible on keyboard focus already. */
                    :focus-visible:not(input):not(select):not(textarea) {
                        outline: 3px solid #0066cc !important;
                        outline-offset: 3px !important;
                    }

                    /* High contrast focus for elements that match background */
                    button:focus-visible,
                    a:focus-visible,
                    [tabindex]:focus-visible {
                        outline: 3px solid #0066cc !important;
                        outline-offset: 3px !important;
                        box-shadow: 0 0 0 6px rgba(0, 102, 204, 0.25) !important;
                    }

                    /* Override problematic outline:none declarations */
                    [data-accordion-trigger]:focus-visible {
                        outline: 3px solid #0066cc !important;
                        outline-offset: 2px !important;
                    }

                    /* Fix breadcrumb links that had matching background outline */
                    .breadcrumbs a:focus-visible,
                    [itemprop="item"]:focus-visible {
                        outline: 3px solid #0066cc !important;
                        outline-offset: 2px !important;
                    }

                    /* Announcement bar buttons */
                    #prevAnnouncement:focus-visible,
                    #nextAnnouncement:focus-visible {
                        outline: 3px solid #ffffff !important;
                        outline-offset: 2px !important;
                        box-shadow: 0 0 0 5px rgba(0, 0, 0, 0.3) !important;
                    }

                    /* Form elements — KEYBOARD focus only. Clicking a field with a
                       mouse leaves it to the field's own focus styling (the brand
                       border tint), which is what Aaron asked for on 2026-09-09.
                       Tab to the same field and the ring is back, so WCAG 2.4.7
                       Focus Visible still holds. */
                    html[data-lag-keyboard-nav] input:focus-visible,
                    html[data-lag-keyboard-nav] select:focus-visible,
                    html[data-lag-keyboard-nav] textarea:focus-visible {
                        outline: 3px solid #0066cc !important;
                        outline-offset: 2px !important;
                        border-color: #0066cc !important;
                    }

                    /* Skip links visibility when focused */
                    .skip-link:focus,
                    [data-acsb-skip-link]:focus {
                        position: fixed !important;
                        top: 10px !important;
                        left: 10px !important;
                        z-index: 999999 !important;
                        background: #000 !important;
                        color: #fff !important;
                        padding: 10px 20px !important;
                        text-decoration: none !important;
                        clip: auto !important;
                        width: auto !important;
                        height: auto !important;
                    }
                `;
                document.head.appendChild(styleElement);
                fixedCount = 1;
                debugLog('Added focus visibility enhancement styles');
            }
        } catch (e) {
            debugLog('Error enhancing focus visibility:', e);
        }

        return fixedCount;
    }

    /**
     * Fix elements with role=list that contain non-listitem children
     * WCAG Issue: "Elements with role=list must contain elements with role=listitem"
     * WCAG 2.2 A 1.3.1
     */
    function fixListRoleChildren() {
        let fixedCount = 0;

        try {
            const roleLists = document.querySelectorAll('[role="list"]');
            roleLists.forEach((list) => {
                const children = list.children;
                for (let i = 0; i < children.length; i++) {
                    const child = children[i];
                    const role = child.getAttribute('role');
                    // If child doesn't have listitem role and isn't already presentation
                    if (role !== 'listitem' && role !== 'presentation' && role !== 'none') {
                        // Check if this is a wrapper that should be a listitem
                        if (child.tagName !== 'LI') {
                            child.setAttribute('role', 'listitem');
                            fixedCount++;
                            debugLog('Fixed list child role:', child);
                        }
                    }
                }
            });
        } catch (e) {
            debugLog('Error fixing list role children:', e);
        }

        debugLog(`Fixed ${fixedCount} list role children`);
        return fixedCount;
    }

    /**
     * Fix iframes missing title attributes
     * WCAG Issue: "iframe and frame elements must have a title attribute"
     * WCAG 2.2 A 4.1.2
     */
    function fixIframeTitles() {
        let fixedCount = 0;

        try {
            const iframes = document.querySelectorAll('iframe:not([title]), iframe[title=""]');
            iframes.forEach((iframe) => {
                const src = iframe.getAttribute('src') || '';
                let title = 'Embedded content';

                // Derive title from source URL
                if (src.includes('youtube')) {
                    title = 'YouTube video player';
                } else if (src.includes('vimeo')) {
                    title = 'Vimeo video player';
                } else if (src.includes('googletagmanager')) {
                    title = 'Google Tag Manager';
                } else if (src.includes('maps.google')) {
                    title = 'Google Maps';
                } else if (src.includes('instagram')) {
                    title = 'Instagram embed';
                } else if (src.includes('facebook')) {
                    title = 'Facebook embed';
                } else if (src.includes('twitter') || src.includes('x.com')) {
                    title = 'Twitter/X embed';
                }

                iframe.setAttribute('title', title);
                fixedCount++;
                debugLog('Fixed iframe title:', iframe);
            });
        } catch (e) {
            debugLog('Error fixing iframe titles:', e);
        }

        debugLog(`Fixed ${fixedCount} iframe titles`);
        return fixedCount;
    }

    /**
     * Run all accessibility fixes
     * @returns {Object} Summary of fixes applied
     */
    function runAllAccessibilityFixes() {
        debugLog('Running all accessibility fixes...');

        const results = {
            emptyHeadings: fixEmptyHeadings(),
            buttonRoleNames: fixButtonRoleAccessibleNames(),
            blankLabels: fixBlankLabels(),
            brokenSkipLinks: fixBrokenSkipLinks(),
            linkNames: fixLinksWithoutAccessibleNames(),
            duplicateLinkText: fixDuplicateLinkText(),
            inlineRatingLinks: nameInlineRatingLinks(),
            nestedInteractive: fixNestedInteractiveControls(),
            externalLinkWarnings: fixExternalLinkWarnings(),
            formInputLabels: fixFormInputLabels(),
            focusVisibility: enhanceFocusVisibility(),
            listRoleChildren: fixListRoleChildren(),
            iframeTitles: fixIframeTitles(),
            timestamp: new Date().toISOString()
        };

        const totalFixes = Object.values(results)
            .filter(v => typeof v === 'number')
            .reduce((a, b) => a + b, 0);

        debugLog(`Accessibility fixes complete. Total fixes applied: ${totalFixes}`);
        debugLog('Fix summary:', results);

        return results;
    }

    // =========================================================================
    // INITIALIZATION
    // =========================================================================

    /**
     * Initialize the accessibility module
     * @param {Object} options - Initialization options
     * @param {boolean} options.autoApplyAlt - Auto-apply alt text on load (default: false)
     * @param {boolean} options.observeDOM - Observe for dynamic images (default: false)
     * @param {boolean} options.autoApplyAccessibilityFixes - Auto-apply all WCAG fixes (default: true)
     * @param {boolean} options.debug - Enable debug logging (default: false)
     */
    function init(options = {}) {
        if (options.debug !== undefined) {
            config.debug = options.debug;
        }

        debugLog('Initializing LAGAccessibility module');

        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                initializeFeatures(options);
            });
        } else {
            initializeFeatures(options);
        }
    }

    /**
     * Initialize features after DOM is ready
     * @param {Object} options - Initialization options
     */
    function initializeFeatures(options) {
        debugLog('initializeFeatures called with options:', options);

        // Apply alt text fixes (existing functionality)
        if (options.autoApplyAlt) {
            // Initial application - may run before product.js filtering
            debugLog('Running initial alt text application...');
            applyAltTextToImages();
            applyAltToProductCards();

            // CRITICAL: Run again after a delay to catch images after product.js filtering
            // product.js.liquid runs on DOMContentLoaded and may take time to filter images
            const delays = [100, 500, 1000, 2000];
            delays.forEach(delay => {
                setTimeout(() => {
                    debugLog(`Running delayed alt text application (${delay}ms)...`);
                    applyAltTextToImages();
                }, delay);
            });

            // Also run when the window is fully loaded (images, scripts, etc.)
            window.addEventListener('load', () => {
                debugLog('Window load event - running alt text application...');
                applyAltTextToImages();

                // One more pass after load to catch any late changes
                setTimeout(() => {
                    debugLog('Post-load alt text application (500ms)...');
                    applyAltTextToImages();
                }, 500);
            });

            // Listen for custom event from product.js when filtering is complete
            document.addEventListener('product:filter-complete', () => {
                debugLog('product:filter-complete event received - reapplying alt text...');
                applyAltTextToImages();
            });

            // Also listen for variant change events
            document.addEventListener('variant:change', () => {
                debugLog('variant:change event received - reapplying alt text...');
                setTimeout(() => applyAltTextToImages(), 100);
            });
        }

        // NEW: Apply all WCAG accessibility fixes from the accessibility report
        if (options.autoApplyAccessibilityFixes !== false) {
            // Run all accessibility fixes by default (opt-out with { autoApplyAccessibilityFixes: false })
            runAllAccessibilityFixes();
            observeInlineRatings();
        }

        if (options.observeDOM) {
            observeAndApplyAlt();
        }

        debugLog('LAGAccessibility initialized successfully');
        debugLog('WCAG 2.2 Accessibility Fixes Applied - Level A, AA, and AAA issues addressed');
    }

    // =========================================================================
    // PUBLIC API
    // =========================================================================

    return {
        // Configuration
        config,

        // Utility functions
        extractFilename,
        normalizeFilename,
        extractSkuFromFilename,
        hasGeneralKeyword,
        findGeneralKeyword,
        findAllGeneralKeywords,
        getKeywordDescription,
        buildKeywordPhrase,
        determineImagePosition,
        humanizeFilename,

        // Product data access
        getProductData,
        getProductVariants,
        getProductMedia,
        getProductTitle,
        findVariantBySku,
        findVariantById,
        extractShadeName,

        // Alt text generation (WCAG 2.2 compliant - no SKUs in output)
        generateImageAlt,
        generateVariantImageAlt,
        isSkuOnlyAlt,
        canEnhanceAlt,

        // DOM manipulation for images
        applyAltTextToImages,
        applyAltToProductCards,
        observeAndApplyAlt,

        // WCAG Accessibility Fixes (Level A, AA, AAA)
        fixEmptyHeadings,
        fixButtonRoleAccessibleNames,
        fixBlankLabels,
        fixBrokenSkipLinks,
        fixLinksWithoutAccessibleNames,
        fixDuplicateLinkText,
        nameInlineRatingLinks,
        fixNestedInteractiveControls,
        fixExternalLinkWarnings,
        fixFormInputLabels,
        enhanceFocusVisibility,
        fixListRoleChildren,
        fixIframeTitles,
        runAllAccessibilityFixes,

        // Initialization
        init,

        // Version - Major update for comprehensive WCAG compliance
        version: '2.0.0'
    };

})();

// Export for module systems if available
if (typeof module !== 'undefined' && module.exports) {
    module.exports = LAGAccessibility;
}

// Auto-initialize with default settings when script loads
// This enables all accessibility fixes automatically
LAGAccessibility.init({ 
    autoApplyAlt: true, 
    observeDOM: true,
    autoApplyAccessibilityFixes: true
});
